import { useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { SortableHandleProps } from '../../../../shared/sortable-list';
import GeneralDialog from '../../../../shared/general-dialog';
import RenameFieldDialog from '../edit-label/rename-field-dialog';
import LockIndicator from '../edit-label/lock-indicator';
import { LockIndex, NO_LOCKS } from '../label-locks';
import FieldOptionsEditor from './field-options-editor';
import FieldTypeIcon from './field-type-icon';
import LabelAppIcon from './label-app-icon';
import {
  CreateLabelForm,
  DATE_FORMAT_OPTIONS,
  DIALOG_CANCEL_BUTTON_SX,
  FIELD_TYPE_CONFIG,
  LABEL_LIMITS,
  LabelFieldForm,
} from './label-schema';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Block from '@mui/icons-material/Block';
import MoreVert from '@mui/icons-material/MoreVert';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

export const SAVE_BEFORE_DELETE_FIELD =
  'Save the draft to delete this disabled field';

interface FieldCardProps {
  control: Control<CreateLabelForm>;
  errors: FieldErrors<CreateLabelForm>;
  field: LabelFieldForm;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  handle: SortableHandleProps;
  isDragging: boolean;
  onDragStateChange: (isDragging: boolean) => void;
  /** Edit Label: the label's Google rule locks. */
  locks?: LockIndex;
  /** Edit Label: opens the Rules step from a lock's popover. */
  onViewRules?: () => void;
}

export default function FieldCard({
  control,
  errors,
  field,
  index,
  expanded,
  onToggle,
  onDelete,
  handle,
  isDragging,
  onDragStateChange,
  locks = NO_LOCKS,
  onViewRules,
}: FieldCardProps) {
  const config = FIELD_TYPE_CONFIG[field.type];
  // The header title only updates when the name input is committed (blur, Enter, or collapse),
  // not on every keystroke.
  const [title, setTitle] = useState(field.name);
  const fieldErrors = errors.fields?.[index];
  const isOpen = expanded && !isDragging;
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  // Edit Label: renaming a live field asks first, like the Google Admin console. The name it was
  // last confirmed as (or saved as) doesn't ask again.
  const [confirmedName, setConfirmedName] = useState(field.savedName ?? '');
  const [renameFrom, setRenameFrom] = useState<string | null>(null);
  const isPublished = !!field.published;
  // A locked option locks its field too: it can't be disabled or deleted.
  const isLocked = locks.isFieldOrOptionLocked(field.id);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const askToConfirmRename = (name: string) => {
    if (isPublished && confirmedName && name.trim() !== confirmedName.trim()) {
      setRenameFrom(confirmedName);
    }
  };

  const handleToggle = () => {
    if (expanded) {
      setTitle(field.name);
    }
    onToggle();
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 1, py: 1, bgcolor: 'action.hover', cursor: 'pointer' }}
        onClick={handleToggle}
      >
        <IconButton
          ref={handle.setActivatorNodeRef}
          {...handle.attributes}
          {...handle.listeners}
          size="small"
          aria-label="Drag to reorder field"
          onClick={(e) => e.stopPropagation()}
          sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
        {/* Edit Label: a disabled field fades its icon, name and apps, like the design. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          data-testid="field-summary"
          sx={{ minWidth: 0, opacity: field.disabled ? 0.45 : 1 }}
        >
          <FieldTypeIcon type={field.type} color="action" />
          <Typography
            sx={{ fontWeight: 500, minWidth: 0 }}
            noWrap
            title={title || 'Untitled'}
          >
            {title.trim() || 'Untitled'}
          </Typography>
          <LabelAppIcon app="DRIVE" />
          {config.gmailSupported && <LabelAppIcon app="GMAIL" />}
        </Stack>
        <Box sx={{ flex: 1 }} />
        {fieldErrors && (
          <Tooltip title="Some fields are required">
            <ErrorOutline
              color="error"
              fontSize="small"
              aria-label="Some fields are required"
            />
          </Tooltip>
        )}
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse field' : 'Expand field'}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        {isLocked && (
          <LockIndicator
            name={title.trim() || 'Field'}
            level="field"
            onViewRules={onViewRules}
          />
        )}
        {isPublished && !isLocked && (
          <Controller
            name={`fields.${index}.disabled`}
            control={control}
            render={({ field: input }) =>
              input.value ? (
                // A disabled field's actions sit in a menu: Enable, or Delete once Google has it
                // disabled.
                <>
                  <IconButton
                    size="small"
                    aria-label={`Actions for field ${
                      title.trim() || 'Untitled'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor(e.currentTarget);
                    }}
                    sx={{ bgcolor: 'action.selected' }}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={!!menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                    // The menu renders in a portal, but its clicks still bubble to the header.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        input.onChange(false);
                      }}
                    >
                      Enable
                    </MenuItem>
                    {field.savedDisabled ? (
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null);
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        Delete
                      </MenuItem>
                    ) : (
                      <Tooltip
                        title={SAVE_BEFORE_DELETE_FIELD}
                        placement="left"
                      >
                        <Box component="span">
                          <MenuItem disabled>Delete</MenuItem>
                        </Box>
                      </Tooltip>
                    )}
                  </Menu>
                </>
              ) : (
                <Tooltip title="Disable field">
                  <IconButton
                    size="small"
                    aria-label="Disable field"
                    onClick={(e) => {
                      e.stopPropagation();
                      input.onChange(true);
                    }}
                  >
                    <Block />
                  </IconButton>
                </Tooltip>
              )
            }
          />
        )}
        {!isPublished && (
          <IconButton
            size="small"
            aria-label="Delete field"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteOutline />
          </IconButton>
        )}
      </Stack>

      <Collapse in={isOpen} unmountOnExit>
        <Stack spacing={2} sx={{ p: 2 }}>
          <Controller
            name={`fields.${index}.name`}
            control={control}
            render={({ field: input }) => (
              <TextField
                {...input}
                id={`field-name-${field.key}`}
                label="Field name"
                required
                fullWidth
                slotProps={{
                  htmlInput: { maxLength: LABEL_LIMITS.maxFieldNameLength },
                }}
                onBlur={() => {
                  input.onBlur();
                  setTitle(input.value);
                  askToConfirmRename(input.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setTitle(input.value);
                  }
                }}
                error={!!fieldErrors?.name}
                helperText={fieldErrors?.name?.message}
              />
            )}
          />
          {field.type === 'DATE' && (
            <Controller
              name={`fields.${index}.dateFormat`}
              control={control}
              render={({ field: input }) => (
                <FormControl
                  fullWidth
                  size="small"
                  required
                  disabled={isPublished}
                >
                  <InputLabel id={`date-format-label-${field.key}`}>
                    Date format
                  </InputLabel>
                  <Select
                    {...input}
                    id={`date-format-${field.key}`}
                    labelId={`date-format-label-${field.key}`}
                    label="Date format"
                  >
                    {DATE_FORMAT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}
          {field.type === 'PERSON' && (
            <Controller
              name={`fields.${index}.multipleSelections`}
              control={control}
              render={({ field: input }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id={`field-multiple-${field.key}`}
                      disabled={isPublished}
                      checked={input.value}
                      onChange={(e) => input.onChange(e.target.checked)}
                    />
                  }
                  label="Allow multiple selections"
                />
              )}
            />
          )}
          <Controller
            name={`fields.${index}.required`}
            control={control}
            render={({ field: input }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    id={`field-required-${field.key}`}
                    checked={input.value}
                    onChange={(e) => input.onChange(e.target.checked)}
                  />
                }
                label={config.requiredLabel}
              />
            )}
          />
          {config.hasOptions && (
            <FieldOptionsEditor
              control={control}
              errors={errors}
              fieldIndex={index}
              fieldType={field.type}
              onDragStateChange={onDragStateChange}
              fieldId={field.id}
              locks={locks}
              onViewRules={onViewRules}
            />
          )}
        </Stack>
      </Collapse>

      {renameFrom !== null && (
        <Controller
          name={`fields.${index}.name`}
          control={control}
          render={({ field: input }) => (
            <RenameFieldDialog
              open
              oldName={renameFrom}
              onCancel={() => {
                input.onChange(renameFrom);
                setTitle(renameFrom);
                setRenameFrom(null);
              }}
              onConfirm={() => {
                setConfirmedName(input.value);
                setRenameFrom(null);
              }}
            />
          )}
        />
      )}

      <GeneralDialog
        open={confirmDeleteOpen}
        handleClose={() => setConfirmDeleteOpen(false)}
        title={`Delete “${title}”?`}
        maxWidth="sm"
        dialogContent={
          <Typography variant="body2">
            This field and any child values will be permanently removed. This
            action cannot be undone.
          </Typography>
        }
        primaryButton={{
          title: 'Delete',
          action: () => {
            setConfirmDeleteOpen(false);
            onDelete();
          },
          loading: false,
          disabled: false,
          color: 'error',
        }}
        secondaryButton={{
          title: 'Cancel',
          action: () => setConfirmDeleteOpen(false),
          loading: false,
          disabled: false,
          // GeneralDialog hides the secondary button unless it's marked shown; the design has Cancel.
          shown: true,
          sx: DIALOG_CANCEL_BUTTON_SX,
        }}
      />
    </Paper>
  );
}
